// Styles
import '@scss/variables.scss';
import '../../MapStyles/MapMenu.scss';

// Libraries
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';

// Store
import { useMapStore } from '../../MapStore/UseMapStore';

// Components (global & local)
import { toast } from '@components';
import { V2FilterMapList } from '../../../../../../components/AssetFilter/functions/V2FilterMapList';
import { MapCreateGateway } from './MapCreateGateway';
import { MapCreatePlace } from './MapCreatePlace';
import { MapCreateStationary } from './MapCreateStationary';
import { MapEditItem } from './MapEditItem';
import { MapEditList } from './MapEditList';
import { MapEditOverlay } from './MapEditOverlay';
import { MapEditTabs } from './MapEditTabs';
import { MapEditTitle } from './MapEditTitle';

export function MapEditMenu() {
  const history = useHistory();

  const mapID = useMapStore((state) => state.mapID);
  const selected = useMapStore((state) => state.selected);
  const showInfo = useMapStore((state) => state.showInfo);
  const showMore = useMapStore((state) => state.showMore);
  const clickEvent = useMapStore((state) => state.clickEvent);
  const setShowInfo = useMapStore((state) => state.setShowInfo);
  const setIsHydrating = useMapStore((state) => state.setIsHydrating);

  // Places
  const placeList = useMapStore((state) => state.placeList);
  const placeFC = useMapStore((state) => state.placeFC);
  const placeLayer = useMapStore((state) => state.placeLayer);
  const setPlaceFC = useMapStore((state) => state.setPlaceFC);
  const setPlaceList = useMapStore((state) => state.setPlaceList);
  const placeRefLayer = useMapStore((state) => state.placeRefLayer);
  const fetchMapPlaces = useMapStore((state) => state.fetchMapPlaces);

  // Gateways
  const gatewayList = useMapStore((state) => state.gatewayList);
  const gatewayFC = useMapStore((state) => state.gatewayFC);
  const gatewayLayer = useMapStore((state) => state.gatewayLayer);
  const setGatewayFC = useMapStore((state) => state.setGatewayFC);
  const setGatewayList = useMapStore((state) => state.setGatewayList);
  const gatewayRefLayer = useMapStore((state) => state.gatewayRefLayer);
  const fetchMapGateways = useMapStore((state) => state.fetchMapGateways);

  // Stationary Assets
  const stationaryList = useMapStore((state) => state.stationaryList);
  const stationaryFC = useMapStore((state) => state.stationaryFC);
  const stationaryLayer = useMapStore((state) => state.stationaryLayer);
  const setStationaryFC = useMapStore((state) => state.setStationaryFC);
  const setStationaryList = useMapStore((state) => state.setStationaryList);
  const stationaryRefLayer = useMapStore((state) => state.stationaryRefLayer);
  const fetchMapStationary = useMapStore((state) => state.fetchMapStationary);

  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [selectedGateway, setSelectedGateway] = useState<number>(null);
  const [curGateways, setCurGateways] = useState<any[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<number>(null);
  const [curAssets, setCurAssets] = useState<any[]>([]);
  const [prevSelected, setPrevSelected] = useState(selected);

  if (selected !== prevSelected) {
    setPrevSelected(selected);
    setIsCreating(false);
  }

  let infoClasses = classNames({
    'info-container': !showInfo,
    'info-container-hide': showInfo,
  });

  let collapseClasses = classNames({
    'collapse-button': showInfo,
    'collapse-button-hide': !showInfo,
  });

  useEffect(() => {
    if (!mapID) return;

    if (placeFC !== null && gatewayFC !== null && stationaryFC !== null) return;

    async function hydrateMapStore() {
      setIsHydrating(true);

      const defaultPayload = V2FilterMapList([], 'and', { field: 'Name', ascending: true }, '');

      try {
        const [places, gateways, stationary] = await Promise.all([
          fetchMapPlaces(defaultPayload),
          fetchMapGateways(defaultPayload),
          fetchMapStationary(defaultPayload),
        ]);

        setPlaceFC(places);
        setPlaceList(places);

        setGatewayFC(gateways);
        setGatewayList(gateways);

        setStationaryFC(stationary);
        setStationaryList(stationary);
      } catch (error) {
        console.error('Failed to hydrate initial map data:', error);
        toast.error('Failed to load map data. Redirecting...');
        history.replace(`/maps/${mapID}`);
      } finally {
        setIsHydrating(false);
      }
    }

    hydrateMapStore();
  }, [mapID, placeFC, gatewayFC, stationaryFC]);

  //Keeps track of gateways placed so duplicates are not possible
  useEffect(() => {
    if (gatewayList) {
      setCurGateways(
        gatewayList.features.map((item) => {
          return parseInt(item.properties['Id']);
        }),
      );
    }
  }, [gatewayList]);

  //Keeps track of stationary Assets placed so duplicates are not possible
  useEffect(() => {
    if (stationaryList) {
      setCurAssets(
        stationaryList.features.map((item) => {
          return parseInt(item.properties['Id']);
        }),
      );
    }
  }, [stationaryList]);

  return (
    <>
      <div className={collapseClasses}>
        <Card style={{ backgroundColor: 'var(--primary)' }}>
          <Button
            variant="dark"
            type="submit"
            className="collapse-card"
            style={{
              borderTopRightRadius: '0px',
              borderBottomRightRadius: '0px',
              width: '50px',
            }}
            onClick={() => {
              setShowInfo(!showInfo);
            }}
          >
            <i
              className={'sonarIcon sonarIcon-right-arrow align-sub fs-lg'}
              style={{
                transform: showInfo ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.5s ease-in-out',
                color: 'var(--white)',
              }}
            />
          </Button>
        </Card>
      </div>
      <div className={infoClasses}>
        <Card className="d-flex flex-row" style={{ backgroundColor: 'var(--primary)' }}>
          <div className="collapse-container">
            <Button
              variant="dark"
              type="submit"
              className="collapse-card"
              style={{
                borderTopRightRadius: '0px',
                borderBottomRightRadius: '0px',
                width: '50px',
              }}
              onClick={() => {
                setShowInfo(!showInfo);
              }}
            >
              <i
                className={'sonarIcon sonarIcon-right-arrow align-sub fs-lg'}
                style={{
                  transform: showInfo ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.5s ease-in-out',
                  color: 'var(--white)',
                }}
              />
            </Button>
          </div>
          <div className="menu-bar">
            <MapEditTitle />
            <MapEditTabs />
          </div>
        </Card>
        <Card
          className={selected === '' ? 'object-list' : 'object-list-expand'}
          style={{
            backgroundColor: 'var(--light)',
            borderTopLeftRadius: '0px',
            borderTopRightRadius: '0px',
          }}
        >
          <MapEditOverlay visible={selected === 'overlay' && showMore === null} />
          <MapEditList
            key="place"
            type="place"
            data={placeFC}
            listData={placeList}
            listMarkers={placeLayer}
            listRefs={placeRefLayer}
            visible={selected === 'place' && showMore === null && isCreating === false}
            setIsCreating={(e) => {
              setIsCreating(e);
            }}
          />
          <MapEditList
            key="gateway"
            type="gateway"
            data={gatewayFC}
            listData={gatewayList}
            listMarkers={gatewayLayer}
            listRefs={gatewayRefLayer}
            visible={selected === 'gateway' && showMore === null && isCreating === false}
            setIsCreating={(e) => {
              setIsCreating(e);
            }}
          />
          <MapEditList
            key="stationary"
            type="stationary"
            data={stationaryFC}
            listData={stationaryList}
            listMarkers={stationaryLayer}
            listRefs={stationaryRefLayer}
            visible={selected === 'stationary' && showMore === null && isCreating === false}
            setIsCreating={(e) => {
              setIsCreating(e);
            }}
          />
          {showMore !== null && clickEvent !== null && <MapEditItem />}
          {isCreating && selected === 'place' && (
            <MapCreatePlace setIsCreating={(e) => setIsCreating(e)} />
          )}
          {isCreating && selected === 'gateway' && (
            <MapCreateGateway
              curGateways={curGateways}
              selectedGateway={selectedGateway}
              setCurGateways={(e) => setCurGateways(e)}
              setSelectedGateway={(e) => setSelectedGateway(e)}
              setIsCreating={(e) => setIsCreating(e)}
            />
          )}
          {isCreating && selected === 'stationary' && (
            <MapCreateStationary
              curAssets={curAssets}
              selectedAsset={selectedAsset}
              setCurAssets={(e) => setCurAssets(e)}
              setSelectedAsset={(e) => setSelectedAsset(e)}
              setIsCreating={(e) => setIsCreating(e)}
            />
          )}
        </Card>
      </div>
    </>
  );
}
